import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { resolveGitHubContext } from '@vivicta/github-config';
import { Button, EmptyState } from '@vivicta/ui';
import { Plug } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';

import type { GitHubConnection } from './useGitHubConnection';

/**
 * Explains why GitHub must be connected and offers the button to do it. Use it
 * as a whole page (through `GitHubConnectionGate`) or inside a card.
 */
export const ConnectGitHubPrompt = ({
  connection,
  reason,
  className,
}: {
  connection: GitHubConnection;
  /** What needs GitHub, completed by "on <host>", e.g. "Repository Map shows the repositories you can open". */
  reason: ReactNode;
  className?: string;
}) => {
  const configApi = useApi(configApiRef);
  const { host } = useMemo(() => resolveGitHubContext(configApi), [configApi]);

  return (
    <EmptyState
      icon={Plug}
      className={className}
      title="Connect your company GitHub account"
      description={
        <>
          {reason} on {host}. The portal works with GitHub as you, so connect
          your company GitHub account to continue.
          {connection.error && (
            <span className="mt-2 block text-destructive-strong">
              {connection.error}
            </span>
          )}
        </>
      }
      action={
        <Button
          type="button"
          className="mt-4"
          disabled={connection.connecting}
          onClick={() => void connection.connect()}
        >
          {connection.connecting ? 'Connecting…' : 'Connect GitHub'}
        </Button>
      }
    />
  );
};
